import { FC } from "react";
import { isObject } from "lodash";

import { JunkSizeEnum, LocationAccessEnum } from "@/domain/models/common";
import { I18nKeyType } from "@/i18n";
import { useTranslator } from "@/i18n/useTranslator";

import { EditableField } from "../components/admin/EditableField";
import { ImgSliderFromPaths } from "../components/ImgsSliderFromPaths";
import { DsButton, DsInput, DsText } from "../components/ui";
import { useServiceQuery } from "../data/queries/serviceQueries";
import { JobServiceTaskBaseType } from "../domain/task";
import { classNames } from "../utils/classNames";

type EditableControlPropsType = {
  value: JunkSizeEnum | LocationAccessEnum;
  onChange: (value: unknown) => void;
  onBlur?: () => void;
};

const LoadSizeControl: React.FC<EditableControlPropsType> = ({
  value,
  onChange,
}) => {
  const { t } = useTranslator();
  return (
    <div className="grid grid-cols-3 w-full">
      <DsButton
        type="button"
        variant={value === JunkSizeEnum.ONE_THIRD ? "primary" : "secondary"}
        onClick={() => onChange(JunkSizeEnum.ONE_THIRD)}
        className="col-span-1">
        1/3
      </DsButton>
      <DsButton
        type="button"
        variant={value === JunkSizeEnum.TWO_THIRDS ? "primary" : "secondary"}
        onClick={() => onChange(JunkSizeEnum.TWO_THIRDS)}
        className="col-span-1">
        2/3
      </DsButton>
      <DsButton
        type="button"
        variant={value === JunkSizeEnum.FULL ? "primary" : "secondary"}
        onClick={() => onChange(JunkSizeEnum.FULL)}
        className="col-span-1 uppercase">
        {t("full")}
      </DsButton>
    </div>
  );
};

const LocationAccessControl: React.FC<EditableControlPropsType> = ({
  value,
  onChange,
}) => {
  const { t } = useTranslator();
  return (
    <div className="grid grid-cols-2 w-full">
      <DsButton
        type="button"
        variant={value === LocationAccessEnum.EASY ? "primary" : "secondary"}
        onClick={() => onChange(LocationAccessEnum.EASY)}
        className="col-span-1 uppercase">
        {t("easy")}
      </DsButton>
      <DsButton
        type="button"
        variant={
          value === LocationAccessEnum.DIFFICULT ? "primary" : "secondary"
        }
        onClick={() => onChange(LocationAccessEnum.DIFFICULT)}
        className="col-span-1 uppercase">
        {t("difficult")}
      </DsButton>
    </div>
  );
};

type DecimalFieldPropsType = {
  onChange: (value: number) => void;
  value?: number;
  className?: string;
  placeholder?: I18nKeyType;
  title?: I18nKeyType;
  min?: number;
  max?: number;
  step?: number;
  error?: boolean | string;
};

const DecimalField = ({
  onChange,
  className,
  step = 0.01,
  ...props
}: DecimalFieldPropsType) => {
  return (
    <DsInput
      type="number"
      step={step}
      className={classNames(
        "py-1 px-2 shadow-xs rounded focus:outline-none focus:shadow-outline",
        className,
      )}
      onChange={e => {
        const value = e.target.value ? parseFloat(e.target.value) : undefined;
        if (value !== undefined) {
          onChange(value);
        }
      }}
      {...props}
    />
  );
};

const DecimalControl: React.FC<EditableControlPropsType> = ({
  value,
  onChange,
}) => (
  <DecimalField
    value={Number(value || 0)}
    onChange={val => onChange(val)}
    className="w-full"
  />
);

type JobDetailJunkSectionPropsType = Omit<JobServiceTaskBaseType, "taskId"> & {
  junkLoadIndex?: number | null;
};

const JobDetailJunkSection: FC<JobDetailJunkSectionPropsType> = ({
  jobId,
  serviceId,
  junkLoadIndex,
}) => {
  const { t } = useTranslator();

  const { data: service } = useServiceQuery({ jobId, serviceId });

  if (!service) {
    return null;
  }

  const hasValidJunkLoadIndex =
    junkLoadIndex !== undefined && junkLoadIndex !== null;

  const data =
    hasValidJunkLoadIndex && service?.junkLoads
      ? service?.junkLoads?.[junkLoadIndex]
      : service;

  return (
    <>
      <div className="py-4 sm:py-2 sm:grid sm:grid-cols-3 sm:gap-4">
        <DsText className="text-sm font-medium text-gray-500">
          {t("jobs_page.service_details.pre_cleanup_photo")}
        </DsText>
        <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
          <div className="flex gap-2">
            {data.preJunkRemovalPhoto && (
              <ImgSliderFromPaths
                className="w-12 h-12 object-cover"
                paths={[
                  isObject(data?.preJunkRemovalPhoto)
                    ? data?.preJunkRemovalPhoto.photoUrl
                    : data?.preJunkRemovalPhoto,
                ]}
              />
            )}
          </div>
        </dd>
      </div>
      <div className="py-4 sm:py-2 sm:grid sm:grid-cols-3 sm:gap-4">
        <DsText className="text-sm font-medium text-gray-500">
          {t("jobs_page.service_details.load_size")}
        </DsText>
        <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
          <EditableField
            path={`jobs/${service.jobId}/services/${service.id}`}
            fieldName="loadSize"
            initialValue={data.loadSize}
            control={LoadSizeControl}
            display={<span className="">{data.loadSize}</span>}
            hideEditButton={hasValidJunkLoadIndex}
          />
        </dd>
      </div>
      <div className="py-4 sm:py-2 sm:grid sm:grid-cols-3 sm:gap-4">
        <DsText className="text-sm font-medium text-gray-500">
          {t("jobs_page.service_details.location_access")}
        </DsText>
        <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2 uppercase">
          <EditableField
            path={`jobs/${service.jobId}/services/${service.id}`}
            fieldName="locationAccess"
            initialValue={data.locationAccess}
            control={LocationAccessControl}
            display={<span className="">{data.locationAccess}</span>}
            hideEditButton={hasValidJunkLoadIndex}
          />
        </dd>
      </div>
      <div className="py-4 sm:py-2 sm:grid sm:grid-cols-3 sm:gap-4">
        <DsText className="text-sm font-medium text-gray-500">
          {t("jobs_page.service_details.post_cleanup_photo")}
        </DsText>
        <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
          <div className="flex gap-2">
            {data.postJunkRemovalPhoto && (
              <ImgSliderFromPaths
                className="w-12 h-12 object-cover"
                paths={[
                  isObject(data?.postJunkRemovalPhoto)
                    ? data?.postJunkRemovalPhoto.photoUrl
                    : data?.postJunkRemovalPhoto,
                ]}
              />
            )}
          </div>
        </dd>
      </div>
      <div className="py-4 sm:py-2 sm:grid sm:grid-cols-3 sm:gap-4">
        <DsText className="text-sm font-medium text-gray-500">
          {t("jobs_page.service_details.weight_surcharge")}
        </DsText>
        <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
          <EditableField
            path={`jobs/${service.jobId}/services/${service.id}`}
            fieldName="weightSurcharge"
            initialValue={data.weightSurcharge}
            control={DecimalControl}
            display={<span className="">{data.weightSurcharge}</span>}
            hideEditButton={hasValidJunkLoadIndex}
          />
        </dd>
      </div>
      <div className="py-4 sm:py-2 sm:grid sm:grid-cols-3 sm:gap-4">
        <DsText className="text-sm font-medium text-gray-500">
          {t("jobs_page.service_details.dump_receipt")}
        </DsText>
        <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
          <div className="flex gap-2">
            {data.dumpReceiptPhotoUrl && (
              <ImgSliderFromPaths
                className="w-12 h-12 object-cover"
                paths={[
                  isObject(data?.dumpReceiptPhotoUrl)
                    ? data?.dumpReceiptPhotoUrl.photoUrl
                    : data?.dumpReceiptPhotoUrl,
                ]}
              />
            )}
          </div>
        </dd>
      </div>
    </>
  );
};

export { JobDetailJunkSection };
